import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Save, Trash2, UserCircle, Upload } from "lucide-react";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

export interface BlogAuthor {
  id: string;
  slug: string;
  name: string;
  photo_url: string | null;
  bio: string | null;
  role: string | null;
}

const slugify = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const AdminAuthors = () => {
  const [authors, setAuthors] = useState<BlogAuthor[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase as any).from("blog_authors").select("*").order("name");
    setAuthors(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = window.prompt("Nom de l'auteur ?");
    if (!name) return;
    const { error } = await (supabase as any).from("blog_authors").insert({
      slug: slugify(name) + "-" + Date.now().toString(36),
      name, role: "", bio: "", photo_url: null,
    });
    if (error) toast.error(error.message); else { toast.success("Auteur créé"); load(); }
  };

  const save = async (a: BlogAuthor) => {
    const { error } = await (supabase as any).from("blog_authors").update({
      name: a.name, role: a.role, bio: a.bio, photo_url: a.photo_url, slug: a.slug,
    }).eq("id", a.id);
    if (error) toast.error(error.message); else toast.success("Enregistré");
  };

  const remove = async (id: string) => {
    if (!confirm("Supprimer cet auteur ?")) return;
    const { error } = await (supabase as any).from("blog_authors").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Supprimé"); load(); }
  };

  const uploadPhoto = async (a: BlogAuthor, file: File) => {
    try {
      const webp = await compressToWebP(file, { maxSizeMB: 0.2 });
      const path = `authors/${a.slug}-${Date.now()}.webp`;
      const { error } = await withUploadTimeout(
        supabase.storage.from("site-images").upload(path, webp, { contentType: webp.type, upsert: true })
      );
      if (error) throw error;
      const { data: pub } = supabase.storage.from("site-images").getPublicUrl(path);
      const url = `${pub.publicUrl}?t=${Date.now()}`;
      await save({ ...a, photo_url: url });
      load();
    } catch (e: any) {
      toast.error(e?.message || "Upload échoué");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <UserCircle className="h-6 w-6 text-primary" /> Auteurs du blog
        </h2>
        <Button onClick={create}><Plus className="mr-2 h-4 w-4" />Nouvel auteur</Button>
      </div>
      {loading && <p className="text-muted-foreground">Chargement…</p>}
      <div className="grid gap-4 md:grid-cols-2">
        {authors.map((a) => (
          <AuthorCard key={a.id} author={a} onChange={(next) => setAuthors((arr) => arr.map((x) => x.id === a.id ? next : x))} onSave={save} onRemove={remove} onUploadPhoto={uploadPhoto} />
        ))}
      </div>
    </div>
  );
};

const AuthorCard = ({ author, onChange, onSave, onRemove, onUploadPhoto }: {
  author: BlogAuthor;
  onChange: (a: BlogAuthor) => void;
  onSave: (a: BlogAuthor) => void;
  onRemove: (id: string) => void;
  onUploadPhoto: (a: BlogAuthor, f: File) => void;
}) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        {author.photo_url ? (
          <img src={author.photo_url} alt={author.name} className="h-14 w-14 rounded-full object-cover border border-border" />
        ) : (
          <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center"><UserCircle className="h-7 w-7 text-muted-foreground" /></div>
        )}
        <CardTitle className="font-heading text-base">{author.name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input value={author.name} onChange={(e) => onChange({ ...author, name: e.target.value })} placeholder="Nom" />
        <Input value={author.role || ""} onChange={(e) => onChange({ ...author, role: e.target.value })} placeholder="Rôle / spécialité" />
        <Input value={author.slug} onChange={(e) => onChange({ ...author, slug: e.target.value })} placeholder="slug" />
        <Textarea rows={3} value={author.bio || ""} onChange={(e) => onChange({ ...author, bio: e.target.value })} placeholder="Bio courte" />
        <Input value={author.photo_url || ""} onChange={(e) => onChange({ ...author, photo_url: e.target.value })} placeholder="URL photo" />
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <Upload className="h-4 w-4" /> Téléverser une photo
          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
            const f = e.target.files?.[0]; if (f) onUploadPhoto(author, f);
          }} />
        </label>
        <div className="flex justify-between pt-2">
          <Button size="sm" variant="ghost" onClick={() => onRemove(author.id)} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-1" /> Supprimer
          </Button>
          <Button size="sm" onClick={() => onSave(author)}>
            <Save className="h-4 w-4 mr-1" /> Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminAuthors;